import React, { useMemo } from 'react';
import { BaseSelector, BaseSelectorProps } from '../BaseSelector';
import { Option } from '../CustomDropdown';
import { FLESCH_KINCAID_LIST } from './KincaidGrade';
import { useKincaidGradeRefinement } from './useKincaidGradeRefinement';
import CustomOptionWithCount from '../CustomOptionWithCount';

interface KincaidGradeWithCountsProps
  extends Omit<BaseSelectorProps, 'label' | 'options'> {
  // If true, will attempt to use Algolia refinement context for counts
  enableCounts?: boolean;
  // Manual override for count data (useful for testing or custom data sources)
  countData?: Array<{ level: number; count: number }>;
}

/**
 * Enhanced KincaidGrade selector that displays counts from Algolia refinements
 */
export const KincaidGradeWithCounts: React.FC<KincaidGradeWithCountsProps> = ({
  enableCounts = false,
  countData,
  ...props
}) => {
  // Always call the hook at top level, but only use it if enableCounts is true
  const kincaidGradeRefinement = useKincaidGradeRefinement();

  const optionsWithCounts = useMemo((): Option[] => {
    // Generate base options from the grade list
    const baseOptions = FLESCH_KINCAID_LIST.map((grade) => ({
      value: grade,
      label: grade,
    }));

    // If counts are not enabled, return base options
    if (!enableCounts) {
      return baseOptions;
    }

    // Add counts from manual data if provided
    if (countData) {
      return baseOptions.map((option) => {
        const level = parseInt(option.value, 10);
        const countItem = countData.find((item) => item.level === level);
        return {
          ...option,
          count: countItem?.count,
        };
      });
    }

    // Add counts from refinement context if available
    if (kincaidGradeRefinement && enableCounts) {
      return baseOptions.map((option) => {
        const level = parseInt(option.value, 10);
        const count = kincaidGradeRefinement.getCountForLevel(level);
        return {
          ...option,
          count: count > 0 ? count : 0,
        };
      });
    }

    // Fallback to base options if no count data available
    return baseOptions;
  }, [enableCounts, countData, kincaidGradeRefinement]);

  // Custom label functions to show counts when available
  const optionLabel = (option: Option) => {
    if (enableCounts && option.count !== undefined) {
      return <CustomOptionWithCount value="" label={option.label} count={option.count} />;
    }
    return `Grade ${option.label}`;
  };

  const selectedLabel = (selected: Option) => `Kincaid Grade: ${selected.label}`;

  return (
    <BaseSelector
      {...props}
      label="Flesch Kincaid Grade"
      options={optionsWithCounts}
      optionLabel={optionLabel}
      selectedLabel={selectedLabel}
      tooltip="Flesch-Kincaid Grade Level - indicates the U.S. school grade level needed to understand the text"
    />
  );
};

export default KincaidGradeWithCounts;
